import { closeDialog, openDialog } from "@/src/components/dialogs";
import Loading from "@/src/components/loading";
import { Patient, PatientDocument } from "@/src/data/allTypes";
import apiFetchStream from "@/src/utils/apiFetchStream";
import { openToast } from "@/src/utils/toasts";
import { decodeStream, getUserId } from "@/src/utils/utils";
import { forwardRef, useEffect, useState } from "react";
import { FaMicrophoneLines } from "react-icons/fa6";
import { openAudioRecorder } from "../aiAssistant/audioRecorder";
import { LinesHighlighter } from "./linesHighlighter";
import { generateAnswersString } from "../fields/fieldsAnswersList";
import { getFullPatientSummaryText } from "./fullPatientSummaryText";

export const AiAudioDocumentEditorButton = forwardRef<
  HTMLDivElement,
  {
    editorRef: React.RefObject<HTMLDivElement>;
    patient: Patient | null | undefined;
    currentPatientDocument: PatientDocument | null | undefined;
    onChange: (
      patientDocument: PatientDocument,
      editedLines: { l: string; v: string }[]
    ) => void;
  }
>(function AiAudioDocumentEditorButton(
  { patient, currentPatientDocument, onChange, editorRef },
  ref
) {
  const [reload, setReload] = useState(0);
  const [transcriptHistory, setTranscriptHistory] = useState([] as string[]);
  const [patientDocument, setPatientDocument] = useState(
    currentPatientDocument
  );

  const [fullPatientSummaryText, setFullPatientSummaryText] = useState("");
  const [aiEdits, setAiEdits] = useState([] as { l: string; v: string }[]);
  const [htmlContent, setHtmlContent] = useState("");
  useEffect(() => {
    loadPatientSummaryText();
  }, []);

  async function loadPatientSummaryText() {
    if (!patient || !currentPatientDocument || !currentPatientDocument.parent)
      return;
    let text = await getFullPatientSummaryText({
      patient,
      parent: currentPatientDocument?.parent,
    });

    // let response = await apiFetch(
    //   "ai",
    //   "summarizeUsingAi",
    //   { currentHtmlContent: text },
    //   "Erreur lors du chargement du résumé"
    // );

    setFullPatientSummaryText(text);
  }

  function handleSelectionMarkers() {
    const sel = window.getSelection();
    if (!sel) return editorRef.current?.innerHTML || "";

    const range = sel.getRangeAt(0);

    // Insert end marker
    const endRange = range.cloneRange();
    endRange.collapse(false);
    const endMarker = document.createElement("span");
    endMarker.setAttribute("data-end", "");
    endRange.insertNode(endMarker);

    // Insert start marker
    const startRange = range.cloneRange();
    startRange.collapse(true);
    const startMarker = document.createElement("span");
    startMarker.setAttribute("data-start", "");
    startRange.insertNode(startMarker);

    // Read HTML
    const htmlWithCursors = (editorRef.current?.innerHTML || "")
      .replace(/<span data-start="[^"]*"><\/span>/g, "[start cursor]")
      .replace(/<span data-end="[^"]*"><\/span>/g, "[end cursor]");

    // Clean up
    startMarker.remove();
    endMarker.remove();
    return htmlWithCursors;
  }

  if (!patient || !patientDocument) return null;

  return (
    <div id="aiAudioDocumentEditorButtonContainer">
      <div
        ref={ref}
        onClick={() => {
          setReload(reload + 1);
          setAiEdits([]);
          setHtmlContent(handleSelectionMarkers());
        }}
      ></div>
      <LinesHighlighter
        content={patientDocument?.content || ""}
        aiEdits={aiEdits}
      />
      <div
        style={{
          position: "fixed",
          top: "20px",
          left: "20px",
          zIndex: 9999999,
        }}
      >
        <div
          className="center"
          style={{
            cursor: "pointer",
            width: "70px",
            height: "70px",
            background: "white",
            borderRadius: "100%",
          }}
          onClick={async () => {
            await openAudioRecorder({
              shouldShowFooter: true,
              onChange: async (transcript, base64Audio) => {
                // transcript= `Alors, on a un nourrisseur de 19 mois qui est admis pour une exploration d'une insuffisance hépatocellulaire aiguë avec une cholestase clinico-biologique. Donc, ce patient n'a pas d'antécédent physiologique ni pathologique particulier. Il est issu d'un mariage consanguin, troisième degré. Une mère âgée de 42 ans sans antécédent particulier, un papa âgé de 58 ans sans antécédent particulier. Pour l'histoire de la maladie, elle remonte à un mois avant son admission, marquée par l'installation d'un hectare cutaniumique suivi d'une cholestase clinique où le diagnostic d'insuffisance hépatocellulaire a été posé. Le patient a été mis sous un régime hippoprotidique avec l'actuleuse et a été orienté à notre niveau pour complément de prise en charge. Donc, l'examen clinique à l'admission, on retrouve un nourrissant en moyen état général, apéritif, stable sur le plan hémodynamique, bonne constante hémodynamique, un hectare cutaniumique généralisé avec un crurit modéré et avec des lésions de grattage. On retrouve un syndrome hémorragique à type de ptc, donc une hépatomie égalie, des selles décolorées, des urines foncées. C'est tout pour l'examen clinique.`;

                console.log("patientDocument", patientDocument);
                openDialog({
                  id: "aiLoadingDialog",
                  title: "Traitement de la requête",
                  content: (
                    <div className="center">
                      <Loading size={30}></Loading>
                    </div>
                  ),
                });

                try {
                  let data = await apiFetchStream(
                    "ai",
                    "aiEditDocument2",
                    {
                      transcriptHistory,
                      fullPatientSummaryText,
                      answersStr: generateAnswersString(
                        patientDocument?.refAnswer,
                        patientDocument?.template?.reference
                      ),
                      htmlContent,
                      patient,
                      base64Audio,
                      text: transcript,
                      userId: getUserId(),
                    },
                    "Erreur lors du chargement des questions"
                  );

                  decodeStream({
                    stream: data as any,
                    parseChunk: (chunk) => {
                      return chunk;
                      // parseOpenAiChunk(chunk) || "";
                    },
                    onStreamComplete: (
                      fullValue,
                      response: {
                        editedLines: {
                          l: string;
                          v: string;
                        }[];
                        audioTranscript: string;
                      } | null
                    ) => {
                      console.log({ fullValue, response });
                      let task = response?.audioTranscript || "";

                      patientDocument.content = getEditedText({
                        originalText: patientDocument?.content || "",
                        editedChunksObject: response?.editedLines || [],
                        shouldRemoveDeleted: true,
                      });
                      setPatientDocument({ ...patientDocument });
                      setTranscriptHistory([...transcriptHistory, task]);
                      setAiEdits(
                        (response?.editedLines || [])?.filter(
                          (line) =>
                            line.v?.toLowerCase() != "deleted" &&
                            line.v?.toLowerCase() != "deleted-"
                        )
                      );
                      onChange(
                        { ...patientDocument },
                        response?.editedLines || []
                      );
                      closeDialog("extractFieldAnswers");
                    },
                    onChunkReceived: (
                      chunk,
                      currentValue,
                      response: {
                        editedLines: {
                          l: string;
                          v: string;
                        }[];
                      } | null
                    ) => {
                      closeDialog("aiLoadingDialog");
                      console.log({ chunk, currentValue, response });
                      if (!response) return;
                      patientDocument.content = getEditedText({
                        originalText: patientDocument?.content || "",
                        editedChunksObject: response.editedLines,
                      });
                      setAiEdits(response?.editedLines || []);
                      onChange(
                        {
                          ...patientDocument,
                        },
                        typeof response.editedLines == "object"
                          ? response.editedLines
                          : []
                      );
                    },
                  });
                } catch (error) {
                  closeDialog("aiLoadingDialog");
                  closeDialog("extractFieldAnswers");
                  console.error(error);
                  openToast(
                    "error",
                    "Erreur lors de l'extraction des réponses"
                  );
                }
              },
            });
          }}
        >
          <div className="center">
            <FaMicrophoneLines
              size={35}
              color={fullPatientSummaryText ? "black" : "gray"}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

function getEditedText({
  originalText,
  editedChunksObject,
  shouldRemoveDeleted,
}: {
  originalText: string;
  editedChunksObject: {
    l: string;
    v: string;
  }[];
  shouldRemoveDeleted?: boolean;
}) {
  const originalChunksObject = getChunks(originalText);
  let chunks = typeof editedChunksObject == "object" ? editedChunksObject : [];

  for (let chunk of chunks) {
    let newValue = chunk.v;
    if (
      shouldRemoveDeleted &&
      (newValue?.toLowerCase() == "deleted" ||
        newValue?.toLowerCase() == "deleted-")
    ) {
      delete originalChunksObject[chunk.l];
      continue;
    }
    if (newValue?.toLowerCase() == "deleted") newValue = "";
    if (newValue?.toLowerCase() == "deleted-") newValue = "";

    originalChunksObject[chunk.l] = newValue;
  }

  return Object.values(originalChunksObject)
    .filter((val) => {
      return true;
    })
    .join("\n");
}

function getChunks(text: string) {
  const obj: Record<string, string> = {};
  let lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    obj[`${i}`] = lines[i];
  }
  return obj;
}
